import { useEffect, useState } from "react";
import {
    Loader2,
    Download,
    Trash2,
    Package,
    AlertTriangle,
    ShieldAlert,
    CheckCircle2,
    Share2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API, tokenStore } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import {
    pickExportDownloadStrategy,
    blobToNamedFile,
    shouldCloseAfterHandoff,
} from "../lib/exportDownloadStrategy";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const PUBLIC_ONLY_HINT =
    "Only items visible to the whole Project Team. Skips anything marked Administrators Only or Restricted.";
const ALL_SCOPE_HINT =
    "Everything I can see right now — including Administrators Only and Restricted items.";

const CHECKBOXES = [
    {
        key: "include_metadata",
        label: "Project metadata, scenes & characters",
        hint: "Always included — the core records.",
        locked: true,
    },
    {
        key: "include_reports",
        label: "Continuity reports (CSV)",
        hint: "Scene-by-character, character-by-scene, gallery index, needs-review list.",
    },
    {
        key: "include_documents",
        label: "Uploaded document files (PDFs, images, references)",
        hint: "Files keep their original filenames; external links live in the index.",
    },
    {
        key: "include_photos_thumb",
        label: "Continuity photos — thumbnails",
        hint: "Small previews. Original camera/QTake filenames preserved.",
    },
    {
        key: "include_photos_full",
        label: "Continuity photos — full resolution",
        hint: "Large download. Recommended only when handing over the whole production.",
        warn: true,
    },
    {
        key: "include_script_pdf",
        label: "Script PDF + revisions",
        hint: "Original draft filenames preserved.",
        sensitive: true,
    },
    {
        key: "include_script_parsed",
        label: "Parsed script text (scene descriptions & excerpts)",
        hint: "The structured copy of the script used by OnSetFlow.",
        sensitive: true,
    },
    {
        key: "include_audit",
        label: "Audit log (governance trail)",
        hint: "Admin-only. Who did what, when.",
    },
];

/**
 * Iter-157 Phase 4 — Export creation dialog.
 *
 * Mode is detected from `project.archived`. Archive mode pre-ticks
 * the full handover set EXCEPT script PDF + parsed text (user's
 * safety tweak — script remains sensitive even at wrap).
 */
export default function ExportDialog({ open, onOpenChange, project, onCreated }) {
    const apiBase = useApiBase();
    const archived = Boolean(project?.archived);
    const scriptLocked = (project?.script_visibility || "everyone") === "owner_admin";
    const defaultName = `${project?.name || "project"}-${new Date()
        .toISOString()
        .slice(0, 16)
        .replace(/[:T]/g, "")
        .replace(/-/g, "")
        .slice(0, 13)}`;

    const [name, setName] = useState(defaultName);
    const [scope, setScope] = useState("all");
    const [submitting, setSubmitting] = useState(false);
    // Iter-158.13.2.19a — Ready state. After a successful create,
    // the dialog stays open on a distinct "Ready" panel that shows
    // the filename + explicit Download / Save-Share action instead
    // of relying on a bare toast + silent auto-download. Cleared
    // when the user closes the dialog or starts a new export.
    const [readyExport, setReadyExport] = useState(null);
    const [handingOff, setHandingOff] = useState(false);

    // Iter-158.13.2.20f.2 #18 — Hard-timeout on the Save/Share spinner.
    // In normal operation `handoff()` always resets `handingOff` via its
    // `finally` block. But some browsers (notably iOS Safari in low-
    // memory sessions) can enter a state where `nav.share()` never
    // resolves and no rejection is ever raised — the user is left
    // staring at a permanent spinner with no way out. A 20-second
    // watchdog is a defensive fallback: it never fires during a
    // healthy handoff, but if it does trigger, we release the spinner
    // and prompt the user to retry from Recent Exports. Client-side
    // UX only — the Export engine is untouched.
    useEffect(() => {
        if (!handingOff) return undefined;
        const t = setTimeout(() => {
            setHandingOff(false);
            toast.error(
                "Save/Share didn't respond in time. Please try again from Recent Exports below.",
            );
        }, 20000);
        return () => clearTimeout(t);
    }, [handingOff]);

    // Iter-158.13.2.20f.2 #18 — Desktop vs mobile CTA copy. On desktop
    // the file always downloads to Downloads via a classic `<a
    // download>` — labelling the button "Save / Share" reads as
    // aspirational (there is no share sheet on desktop Safari/Chrome).
    // On mobile browsers that expose the Web Share API we DO route
    // through the share sheet so "Save / Share" is honest. We detect
    // mobile via a coarse-pointer media query, which is the
    // Playwright-friendly, deterministic signal (userAgent parsing is
    // brittle across iPadOS Safari that reports as macOS). Falls back
    // to "Download" when the query isn't available.
    const isMobileHandoff = (() => {
        try {
            if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
                return false;
            }
            return window.matchMedia("(pointer: coarse)").matches
                && typeof navigator !== "undefined"
                && typeof navigator.canShare === "function";
        } catch {
            return false;
        }
    })();

    // Mode-aware defaults.
    const buildDefaultConfig = () => ({
        include_metadata: true,
        include_reports: true,
        include_documents: archived,
        include_photos_thumb: archived,
        include_photos_full: false,
        include_script_pdf: false,
        include_script_parsed: false,
        include_audit: archived,
    });
    const [config, setConfig] = useState(buildDefaultConfig);

    useEffect(() => {
        if (open) {
            setName(defaultName);
            setScope("all");
            setConfig(buildDefaultConfig());
            setReadyExport(null);
            setHandingOff(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, project?.id, project?.archived]);

    const toggle = (key) => {
        setConfig((c) => ({ ...c, [key]: !c[key] }));
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        setSubmitting(true);
        try {
            const { data } = await api.post(
                `${apiBase}/${project.id}/exports`,
                {
                    name: name.trim() || null,
                    visibility_scope: scope,
                    ...config,
                },
            );
            // Iter-158.13.2.19a — do NOT close the dialog on success.
            // The old flow silently landed the user back on the
            // Settings page with just an "Export ready" toast; on
            // iOS UAT that read as "nothing happened". We now stay
            // on a distinct Ready panel that shows the filename +
            // size + an explicit Download / Save-Share button so
            // the handoff state is unambiguous.
            setReadyExport(data);
            if (onCreated) onCreated(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    // Shared handoff pipeline. Uses `pickExportDownloadStrategy` to
    // choose between the Web Share sheet (iOS Safari 15+, Android
    // Chrome 80+, small payloads) and the classic blob `<a download>`
    // fallback (everywhere else). Toasts confirm the outcome by
    // filename — never claiming OSF picked the save location, which
    // no browser exposes to JS on any platform.
    //
    // Iter-158.13.2.19b — returns a distinct outcome string
    // (`"shared" | "downloaded" | "cancelled" | "failed"`) so the
    // Ready-panel button can auto-dismiss the dialog on confirmed
    // successful handoff only. Cancel + failure keep the dialog
    // open so the user can retry from the same Ready state.
    const handoff = async (row) => {
        if (!row) return "failed";
        setHandingOff(true);
        try {
            const res = await fetch(
                `${API}${apiBase}/${project.id}/exports/${row.id}/download`,
                { headers: { Authorization: `Bearer ${tokenStore.get()}` } },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const filename = row.filename || `${row.name || "export"}.zip`;
            const file = blobToNamedFile(blob, filename);

            const nav = typeof navigator !== "undefined" ? navigator : {};
            let canShareFiles = false;
            try {
                canShareFiles =
                    typeof nav.canShare === "function" &&
                    typeof File === "function" &&
                    nav.canShare({ files: [file] });
            } catch {
                canShareFiles = false;
            }
            const strategy = pickExportDownloadStrategy({
                sizeBytes: row.size_bytes || blob.size,
                capabilities: {
                    share: nav.share,
                    canShare: nav.canShare,
                    canShareFiles,
                },
            });

            if (strategy === "share") {
                try {
                    await nav.share({
                        files: [file],
                        title: filename,
                        text: "OnSetFlow export",
                    });
                    toast.success(
                        `Shared ${filename}. Your device chose the destination.`,
                    );
                    return "shared";
                } catch (shareErr) {
                    // User cancelled the share sheet OR the browser
                    // silently refused a valid canShare — fall
                    // through to the reliable blob download rather
                    // than leave them stuck.
                    if (shareErr && shareErr.name === "AbortError") {
                        return "cancelled"; // user cancelled — dialog stays open
                    }
                }
            }
            // Blob download fallback (also the primary desktop path).
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(
                `Downloaded ${filename}. Check your device's Downloads or Files app.`,
            );
            return "downloaded";
        } catch (err) {
            toast.error(
                `Could not hand off the export. Check your connection and try again from Recent Exports below.`,
            );
            return "failed";
        } finally {
            setHandingOff(false);
        }
    };

    const showScriptWarning =
        (config.include_script_pdf || config.include_script_parsed) && scriptLocked;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="export-dialog"
                className="max-w-lg bg-neutral-900 border border-white/10 max-h-[90vh] flex flex-col overflow-hidden p-0"
            >
                <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        {readyExport ? (
                            <>
                                <CheckCircle2 size={16} className="text-emerald-300" />
                                Export ready
                            </>
                        ) : (
                            <>
                                <Package size={16} className="text-neutral-300" />
                                {archived ? "Wrap export" : "Create export"}
                            </>
                        )}
                    </DialogTitle>
                    <DialogDescription className="text-[12px] text-neutral-400 leading-relaxed">
                        {readyExport
                            ? "Your export is built and stored under Recent Exports below — you can download it again any time without regenerating."
                            : "Bundle this project into a ZIP that can be opened without OnSetFlow. Original filenames are preserved on photos, documents and scripts."}
                    </DialogDescription>
                </DialogHeader>

                {readyExport ? (
                    /* Iter-158.13.2.19a — Ready panel.
                       Explicit filename, size, and Save/Share button
                       replace the previous "silent toast → auto
                       browser download" handoff that failed iOS UAT.
                       The user must tap Save/Share to trigger the
                       download; no automatic navigation. The Recent
                       Exports list further down the settings page
                       keeps this row available for repeat downloads
                       without regenerating the export. */
                    <div
                        data-testid="export-ready-panel"
                        className="px-6 pb-6 pt-2 space-y-4"
                    >
                        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-2">
                            <div className="flex items-center gap-2">
                                <Package size={14} className="text-emerald-300 shrink-0" />
                                <div
                                    data-testid="export-ready-filename"
                                    className="text-sm font-medium text-neutral-100 truncate"
                                    title={readyExport.filename || `${readyExport.name || "export"}.zip`}
                                >
                                    {readyExport.filename || `${readyExport.name || "export"}.zip`}
                                </div>
                            </div>
                            <div className="text-[11px] text-neutral-400">
                                {formatBytes(readyExport.size_bytes)}
                                {readyExport.wrap_export ? (
                                    <span className="ml-2 text-amber-300/80 uppercase tracking-[0.2em] text-[10px]">
                                        wrap
                                    </span>
                                ) : null}
                            </div>
                            {readyExport.script_skipped_reason ? (
                                <div className="text-[11px] text-amber-300/90">
                                    Script content was not included (you don&apos;t have access to this project&apos;s script).
                                </div>
                            ) : null}
                        </div>
                        <p className="text-[11px] text-neutral-400 leading-relaxed">
                            Tap Save / Share to hand the file off to your device. Your browser or operating system chooses where the file lands — OnSetFlow doesn&apos;t know or control the final save location.
                        </p>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                data-testid="export-ready-download"
                                onClick={async () => {
                                    // Iter-158.13.2.19b — auto-dismiss
                                    // the dialog ONLY on confirmed
                                    // successful handoff. Cancel of
                                    // the native share sheet
                                    // ("cancelled") and network /
                                    // filesystem failures ("failed")
                                    // keep the dialog open so the
                                    // user can retry from the same
                                    // Ready state — matches the
                                    // spec of "success toast could
                                    // be missed; persistent UI must
                                    // make completion obvious".
                                    const outcome = await handoff(readyExport);
                                    if (shouldCloseAfterHandoff(outcome)) {
                                        onOpenChange(false);
                                    }
                                }}
                                disabled={handingOff}
                                className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-neutral-950 gap-2"
                            >
                                {handingOff ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : isMobileHandoff ? (
                                    <Share2 size={14} />
                                ) : (
                                    <Download size={14} />
                                )}
                                {isMobileHandoff ? "Save / Share" : "Download"}
                            </Button>
                            <Button
                                type="button"
                                data-testid="export-ready-close"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                className="text-neutral-300 hover:text-white"
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                ) : null}

                {readyExport ? null : (
                /* Iter-157G P1 — Export modal overflow fix.
                    Outer DialogContent becomes a flex column with
                    `max-h-[90vh]`; the form below becomes the
                    scrollable region so toggling every option
                    still leaves the Submit / Cancel footer visible
                    on small screens. */
                <form onSubmit={submit} className="flex-1 min-h-0 flex flex-col">
                    <div className="flex-1 overflow-y-auto px-6 pt-2 pb-4 space-y-4">
                    <div className="space-y-1.5">
                        <Label
                            htmlFor="export-name"
                            className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Export name
                        </Label>
                        <Input
                            id="export-name"
                            data-testid="export-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="project-YYYYMMDD-HHMM"
                            className="bg-secondary border-transparent text-sm"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Visibility scope
                        </Label>
                        <div className="space-y-2">
                            <label
                                data-testid="export-scope-all"
                                className="flex items-start gap-3 rounded-md border border-white/10 bg-secondary/40 p-3 cursor-pointer hover:bg-secondary/60"
                            >
                                <input
                                    type="radio"
                                    name="scope"
                                    value="all"
                                    checked={scope === "all"}
                                    onChange={() => setScope("all")}
                                    className="mt-1"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-medium">Everything I can see</div>
                                    <div className="text-[11px] text-neutral-400 mt-0.5">
                                        {ALL_SCOPE_HINT}
                                    </div>
                                </div>
                            </label>
                            <label
                                data-testid="export-scope-public"
                                className="flex items-start gap-3 rounded-md border border-white/10 bg-secondary/40 p-3 cursor-pointer hover:bg-secondary/60"
                            >
                                <input
                                    type="radio"
                                    name="scope"
                                    value="public_only"
                                    checked={scope === "public_only"}
                                    onChange={() => setScope("public_only")}
                                    className="mt-1"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-medium">
                                        Project Team items only
                                    </div>
                                    <div className="text-[11px] text-neutral-400 mt-0.5">
                                        {PUBLIC_ONLY_HINT}
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            What to include
                        </Label>
                        <div className="space-y-1.5">
                            {CHECKBOXES.map((c) => {
                                const checked = c.locked
                                    ? true
                                    : Boolean(config[c.key]);
                                return (
                                    <label
                                        key={c.key}
                                        data-testid={`export-toggle-${c.key}`}
                                        className={`flex items-start gap-3 rounded-md border border-white/10 p-2.5 ${
                                            c.locked
                                                ? "bg-secondary/20 cursor-default opacity-90"
                                                : "bg-secondary/40 cursor-pointer hover:bg-secondary/60"
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={c.locked}
                                            onChange={() => !c.locked && toggle(c.key)}
                                            className="mt-1"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sm flex items-center gap-2">
                                                {c.label}
                                                {c.sensitive ? (
                                                    <ShieldAlert
                                                        size={11}
                                                        className="text-amber-400/80"
                                                        title="Sensitive — explicit opt-in"
                                                    />
                                                ) : null}
                                            </div>
                                            <div className="text-[11px] text-neutral-400 mt-0.5">
                                                {c.hint}
                                            </div>
                                            {c.warn && checked ? (
                                                <div className="text-[11px] text-amber-300/90 mt-1">
                                                    Full-resolution exports can
                                                    be large — only include them
                                                    when handing over the whole
                                                    production.
                                                </div>
                                            ) : null}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {showScriptWarning ? (
                        <div
                            data-testid="export-script-warning"
                            className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3"
                        >
                            <AlertTriangle
                                size={14}
                                className="text-amber-300 mt-0.5 shrink-0"
                            />
                            <div className="text-[12px] text-amber-100 leading-relaxed">
                                This project&apos;s script is restricted to{" "}
                                <strong>Administrators Only</strong>. Including
                                it here will share the script (and parsed text,
                                if ticked) with everyone who receives this ZIP.
                            </div>
                        </div>
                    ) : null}
                    </div>

                    {/* Iter-157G P1 — pinned footer so the Submit /
                        Cancel buttons stay visible no matter how
                        many options the user has toggled. */}
                    <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/5 bg-card/60 shrink-0">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                            data-testid="export-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            data-testid="export-submit"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={14} className="animate-spin mr-2" />
                                    Preparing…
                                </>
                            ) : (
                                <>
                                    <Package size={14} className="mr-2" />
                                    Create export
                                </>
                            )}
                        </Button>
                    </div>
                </form>
                )}
            </DialogContent>
        </Dialog>
    );
}


function formatBytes(n) {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}


export function RecentExportsList({ projectId, refreshKey, onChange }) {
    const apiBase = useApiBase();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [deletingId, setDeletingId] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get(`${apiBase}/${projectId}/exports`);
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(formatApiError(e));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, refreshKey]);

    const [downloadingId, setDownloadingId] = useState(null);
    const onDownload = async (row) => {
        // Iter-158.13.2.19a — same strategy-aware handoff pipeline
        // as the ExportDialog Ready panel. Uses the Web Share sheet
        // when the browser + payload size allow (iOS Safari 15+ /
        // Android Chrome 80+, ≤50MB); otherwise falls back to the
        // classic authenticated-blob `<a download>` path. Toasts
        // confirm the outcome by filename — never claiming OSF
        // chose the destination.
        setDownloadingId(row.id);
        try {
            const res = await fetch(
                `${API}${apiBase}/${projectId}/exports/${row.id}/download`,
                { headers: { Authorization: `Bearer ${tokenStore.get()}` } },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const filename = row.filename || `${row.name || "export"}.zip`;
            const file = blobToNamedFile(blob, filename);

            const nav = typeof navigator !== "undefined" ? navigator : {};
            let canShareFiles = false;
            try {
                canShareFiles =
                    typeof nav.canShare === "function" &&
                    typeof File === "function" &&
                    nav.canShare({ files: [file] });
            } catch {
                canShareFiles = false;
            }
            const strategy = pickExportDownloadStrategy({
                sizeBytes: row.size_bytes || blob.size,
                capabilities: {
                    share: nav.share,
                    canShare: nav.canShare,
                    canShareFiles,
                },
            });

            if (strategy === "share") {
                try {
                    await nav.share({
                        files: [file],
                        title: filename,
                        text: "OnSetFlow export",
                    });
                    toast.success(
                        `Shared ${filename}. Your device chose the destination.`,
                    );
                    return;
                } catch (shareErr) {
                    if (shareErr && shareErr.name === "AbortError") return;
                }
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(
                `Downloaded ${filename}. Check your device's Downloads or Files app.`,
            );
        } catch (e) {
            toast.error(
                "Could not download the export. Check your connection and try again.",
            );
        } finally {
            setDownloadingId(null);
        }
    };

    const onDelete = async (row) => {
        if (!window.confirm(`Delete export "${row.name}"? This cannot be undone.`)) return;
        setDeletingId(row.id);
        try {
            await api.delete(`${apiBase}/${projectId}/exports/${row.id}`);
            toast.success("Export deleted");
            await load();
            if (onChange) onChange();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeletingId(null);
        }
    };

    if (error) {
        return (
            <div
                data-testid="recent-exports-error"
                className="text-[12px] text-red-300 mt-3"
            >
                {error}
            </div>
        );
    }
    if (rows === null) {
        return (
            <div className="text-[12px] text-neutral-500 mt-3 flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Loading recent exports…
            </div>
        );
    }
    if (!rows.length) {
        return (
            <div
                data-testid="recent-exports-empty"
                className="text-[12px] text-neutral-500 mt-3 italic"
            >
                No exports yet. Create one above &mdash; they appear here after they&apos;re built.
            </div>
        );
    }
    return (
        <ul
            data-testid="recent-exports-list"
            className="mt-4 divide-y divide-white/5 border border-white/10 rounded-lg overflow-hidden"
        >
            {rows.map((r) => (
                <li
                    key={r.id}
                    data-testid={`export-row-${r.id}`}
                    className="flex items-start gap-3 p-3 bg-secondary/30"
                >
                    <Package
                        size={14}
                        className="text-neutral-400 mt-1 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                            {r.name || r.id}
                            {r.wrap_export ? (
                                <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-amber-300/80">
                                    wrap
                                </span>
                            ) : null}
                        </div>
                        <div className="text-[11px] text-neutral-400 truncate">
                            {r.created_at?.slice(0, 16).replace("T", " · ")}{" "}
                            · by {r.created_by_name || "—"} · {formatBytes(r.size_bytes)}
                        </div>
                        {r.script_skipped_reason ? (
                            <div className="text-[11px] text-amber-300/90 mt-1">
                                Script content was not included (you don&apos;t have
                                access to this project&apos;s script).
                            </div>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        data-testid={`export-download-${r.id}`}
                        onClick={() => onDownload(r)}
                        disabled={downloadingId === r.id}
                        aria-label="Download export"
                        className="tap inline-flex items-center justify-center rounded-md h-9 w-9 text-neutral-300 hover:text-violet-200 hover:bg-violet-500/10 disabled:opacity-50"
                    >
                        {downloadingId === r.id ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <Download size={14} />
                        )}
                    </button>
                    <button
                        type="button"
                        data-testid={`export-delete-${r.id}`}
                        onClick={() => onDelete(r)}
                        disabled={deletingId === r.id}
                        aria-label="Delete export"
                        className="tap inline-flex items-center justify-center rounded-md h-9 w-9 text-neutral-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                        {deletingId === r.id ? (
                            <Loader2 size={12} className="animate-spin" />
                        ) : (
                            <Trash2 size={12} />
                        )}
                    </button>
                </li>
            ))}
        </ul>
    );
}
